import type { CalendarDateTime } from '@internationalized/date';
import { Button, Card, Description } from '@heroui/react';
import type { AvailabilityWindow } from '../../../api/my-parking-spots';
import {
  addHoursToCalendarDateTime,
  addMinutesToCalendarDateTime,
  calendarDateTimeToIsoUtc,
  formatDateTimeBR,
  getCurrentCalendarDateTime,
} from '../../../utils/date-utils';
import { clampDateTimeToWindowBounds } from '../utils/availability-windows';

export const AVAILABILITY_DURATION_PRESETS = [
  { id: '1h', label: '1 h', hours: 1 },
  { id: '2h', label: '2 h', hours: 2 },
  { id: '3h', label: '3 h', hours: 3 },
  { id: '6h', label: '6 h', hours: 6 },
  { id: '1d', label: '1 dia', hours: 24 },
] as const;

export type AvailabilityDurationPresetId =
  (typeof AVAILABILITY_DURATION_PRESETS)[number]['id'];

export function computePresetWindow(
  hours: number,
  activeWindows: AvailabilityWindow[],
  excludeWindowId?: string,
): { startAt: CalendarDateTime; endAt: CalendarDateTime } {
  const now = getCurrentCalendarDateTime();
  let startAt = addMinutesToCalendarDateTime(now, 5);

  const startClamped = clampDateTimeToWindowBounds(startAt, 'start', activeWindows, {
    excludeWindowId,
    absoluteMin: addMinutesToCalendarDateTime(now, 1),
  });
  startAt = startClamped.value;

  let endAt = addHoursToCalendarDateTime(startAt, hours);
  const endClamped = clampDateTimeToWindowBounds(endAt, 'end', activeWindows, {
    excludeWindowId,
    absoluteMin: addMinutesToCalendarDateTime(startAt, 1),
    startAtReference: startAt,
  });
  endAt = endClamped.value;

  return { startAt, endAt };
}

interface AvailabilityDurationPresetsProps {
  selectedPresetId: AvailabilityDurationPresetId | null;
  startAt: CalendarDateTime | null;
  endAt: CalendarDateTime | null;
  activeWindows?: AvailabilityWindow[];
  excludeWindowId?: string;
  title?: string;
  description?: string;
  onSelectPreset: (
    presetId: AvailabilityDurationPresetId,
    startAt: CalendarDateTime,
    endAt: CalendarDateTime,
  ) => void;
}

export function AvailabilityDurationPresets({
  selectedPresetId,
  startAt,
  endAt,
  activeWindows = [],
  excludeWindowId,
  title = 'Disponibilizar por',
  description = 'A partir de agora, pela duração escolhida.',
  onSelectPreset,
}: AvailabilityDurationPresetsProps) {
  return (
    <Card variant="secondary">
      <Card.Header className="border-b border-separator pb-3">
        <Card.Title className="text-base font-semibold">{title}</Card.Title>
        <Card.Description>{description}</Card.Description>
      </Card.Header>
      <Card.Content className="flex flex-col gap-3 pt-4">
        <div className="flex flex-wrap gap-2">
          {AVAILABILITY_DURATION_PRESETS.map((preset) => (
            <Button
              key={preset.id}
              size="sm"
              variant={selectedPresetId === preset.id ? 'primary' : 'secondary'}
              onPress={() => {
                const window = computePresetWindow(preset.hours, activeWindows, excludeWindowId);
                onSelectPreset(preset.id, window.startAt, window.endAt);
              }}
            >
              {preset.label}
            </Button>
          ))}
        </div>
        {startAt && endAt ? (
          <Description className="text-center text-xs">
            De <strong>{formatDateTimeBR(calendarDateTimeToIsoUtc(startAt))}</strong> até{' '}
            <strong>{formatDateTimeBR(calendarDateTimeToIsoUtc(endAt))}</strong>
          </Description>
        ) : null}
      </Card.Content>
    </Card>
  );
}
